// Sector heat map rules — Story #6837 Stream 11b. Pure functions, no React.
//
// One row per ISIC section that is the PRIMARY sector of at least one invention the
// aggregator may see. Everything is read from GET /market/sectors?scope=portfolio:
// the plaintext summary (sectors), the per-invention assessment and SOM midpoint the
// backend fills only where the caller may read the invention in full, and one
// AUS and one WLD reference value per section.
import type {
  MarketPortfolioReferenceValue, MarketPortfolioSectors, MarketTaxonomy,
} from '../../types/market'
import { currencyPrefix, money } from './marketFormat'

export interface SomTotal { currency: string; total: number }

export interface HeatRow {
  section: string
  name: string
  inventions: number
  /** Inventions whose assessment is a number (None = never assessed / insufficient basis). */
  assessedCount: number
  /** Mean of the numeric assessments, or null when none of the row's inventions has one. */
  avgAssessment: number | null
  au: MarketPortfolioReferenceValue | null
  world: MarketPortfolioReferenceValue | null
  /** Σ SOM midpoint per currency: ranges in different currencies are never added together. */
  som: SomTotal[]
  /** Inventions that contributed to the SOM total. */
  somCount: number
}

export interface HeatModel {
  rows: HeatRow[]
  /** Inventions in the portfolio with no primary sector (not yet classified, or insufficient basis). */
  unclassified: number
  /** All inventions in the portfolio payload. */
  total: number
  /** Earliest and latest date of the AI analysis counted in the table, or null when the payload carries none. */
  aiDates: { from: string; to: string } | null
}

export type SortKey = 'assessment' | 'inventions' | 'sector'

export function buildHeatModel(data: MarketPortfolioSectors, taxonomy: MarketTaxonomy | undefined): HeatModel {
  const names = new Map((taxonomy?.sections ?? []).map((s) => [s.code, s.name]))
  const acc = new Map<string, { n: number; scores: number[]; som: Map<string, number>; somCount: number }>()
  let unclassified = 0
  const aiAt: string[] = []
  const projects = Object.values(data.projects)
  for (const p of projects) {
    const primary = p.sectors.find((s) => s.role === 'primary')?.section
    if (!primary) { unclassified += 1; continue }
    const a = acc.get(primary) ?? { n: 0, scores: [], som: new Map<string, number>(), somCount: 0 }
    a.n += 1
    if (typeof p.assessment === 'number') {
      a.scores.push(p.assessment)
      if (p.assessment_at) aiAt.push(p.assessment_at)
    }
    if (typeof p.som_mid === 'number' && p.som_currency) {
      if (p.som_at) aiAt.push(p.som_at)
      a.som.set(p.som_currency, (a.som.get(p.som_currency) ?? 0) + p.som_mid)
      a.somCount += 1
    }
    acc.set(primary, a)
  }
  const rows: HeatRow[] = [...acc.entries()].map(([section, a]) => ({
    section,
    name: names.get(section) ?? section,
    inventions: a.n,
    assessedCount: a.scores.length,
    avgAssessment: a.scores.length ? a.scores.reduce((x, y) => x + y, 0) / a.scores.length : null,
    au: data.reference[section]?.AUS ?? null,
    world: data.reference[section]?.WLD ?? null,
    som: [...a.som.entries()].map(([currency, total]) => ({ currency, total })).sort((x, y) => x.currency.localeCompare(y.currency)),
    somCount: a.somCount,
  }))
  aiAt.sort()
  return {
    rows: sortRows(rows, 'assessment'),
    unclassified,
    total: projects.length,
    aiDates: aiAt.length ? { from: aiAt[0], to: aiAt[aiAt.length - 1] } : null,
  }
}

/** Highest first; rows with no assessment last; ties break on section letter so the order is stable. */
export function sortRows(rows: HeatRow[], key: SortKey): HeatRow[] {
  const byLetter = (a: HeatRow, b: HeatRow) => a.section.localeCompare(b.section)
  const out = [...rows]
  if (key === 'sector') return out.sort(byLetter)
  if (key === 'inventions') return out.sort((a, b) => b.inventions - a.inventions || byLetter(a, b))
  return out.sort((a, b) => {
    if (a.avgAssessment === null && b.avgAssessment === null) return byLetter(a, b)
    if (a.avgAssessment === null) return 1
    if (b.avgAssessment === null) return -1
    return b.avgAssessment - a.avgAssessment || byLetter(a, b)
  })
}

/** 0 (lowest) … 4 (highest) of five equal steps across [min, max]; a flat range sits in the middle. */
export function scaleStep(value: number, min: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (!(max > min)) return 2
  const step = Math.floor(((value - min) / (max - min)) * 5)
  return Math.max(0, Math.min(4, step)) as 0 | 1 | 2 | 3 | 4
}

/** The assessment is scored 0–10, so its five steps are fixed (not relative to the table). */
export const assessmentStep = (score: number): 0 | 1 | 2 | 3 | 4 => scaleStep(score, 0, 10)

/** "A$8.2bn + US$390.0m", or null when no invention in the row has a readable range. */
export function somText(som: SomTotal[]): string | null {
  return som.length ? som.map((s) => money(s.total, currencyPrefix(s.currency))).join(' + ') : null
}
